import React from 'react';
import { Save, X, Send, RotateCcw, CheckCircle, XCircle } from 'lucide-react';
import PrimaryButton from '../PrimaryButton';
import PrimaryButtonLight from '../PrimaryButtonLight';

interface FormButtonBarProps {
  onCancel: () => void;
  onSave?: () => void;
  onSubmit?: () => void;
  onReopen?: () => void;
  onApprove?: () => void;
  saving?: boolean;
  submitting?: boolean;
  reopening?: boolean;
  isEditing?: boolean;
  disabled?: boolean;
  showSubmit?: boolean;
  showReopen?: boolean;
  assessmentStatus?: string;
  className?: string;
  saveText?: string;
  submitText?: string;
  reopenText?: string;
  cancelText?: string;
}

const FormButtonBar: React.FC<FormButtonBarProps> = ({
  onCancel,
  onSave,
  onSubmit,
  onReopen,
  onApprove,
  saving = false,
  submitting = false,
  reopening = false,
  isEditing = false,
  disabled = false,
  showSubmit = true,
  showReopen = false,
  assessmentStatus = 'DRAFT',
  className = '',
  saveText,
  submitText,
  reopenText,
  cancelText = 'Cancel'
}) => {
  // Determine button texts
  const getSaveText = () => {
    if (saveText) return saveText;
    return 'Save';
  };

  const getSubmitText = () => {
    if (submitText) return submitText;
    return 'Submit';
  };

  const getReopenText = () => {
    if (reopenText) return reopenText;
    return 'Reopen';
  };

  // Determine if Save and Submit buttons should be disabled based on status
  const isSaveDisabled = saving || disabled || assessmentStatus === 'SUBMITTED' || assessmentStatus === 'QA' || assessmentStatus === 'COMPLETE';
  const isSubmitDisabled = submitting || disabled;
  
  // Determine button visibility based on status (matching charting workflow)
  // DRAFT -> Save, Submit
  // SUBMITTED, QA -> Reject, Approve
  // COMPLETE -> Reopen
  const shouldShowSave = onSave && (!assessmentStatus || assessmentStatus === 'DRAFT');
  const shouldShowSubmit = showSubmit && assessmentStatus === 'DRAFT' && onSubmit;
  const shouldShowReject = (assessmentStatus === 'SUBMITTED' || assessmentStatus === 'QA') && onReopen;
  const shouldShowApprove = (assessmentStatus === 'SUBMITTED' || assessmentStatus === 'QA') && onApprove;
  const shouldShowReopen = assessmentStatus === 'COMPLETE' && onReopen;
  
  // Check if any action is in progress
  const isAnyActionInProgress = saving || submitting || reopening;

  return (
    <div className={`flex justify-end space-x-3 ${className}`}>
      <PrimaryButtonLight 
        type="button"
        onClick={onCancel}
        disabled={isAnyActionInProgress || disabled}
      >
        <div className="flex items-center">
          <X size={16} className="mr-1" /> 
          <span>{cancelText}</span>
        </div>
      </PrimaryButtonLight>
      
      {shouldShowSave && (
        <PrimaryButton 
          type="button"
          onClick={onSave}
          disabled={isSaveDisabled}
          data-form-save-button="true"
        >
          {saving ? (
            <div className="flex items-center">
              <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{isEditing ? 'Updating...' : 'Saving...'}</span>
            </div>
          ) : (
            <div className="flex items-center">
              <Save size={16} className="mr-1" /> 
              <span>{getSaveText()}</span>
            </div>
          )}
        </PrimaryButton>
      )}
      
      {shouldShowSubmit && (
        <PrimaryButton 
          type="button"
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          className="bg-green-600 hover:bg-green-700"
        >
          {submitting ? (
            <div className="flex items-center">
              <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Submitting...</span>
            </div>
          ) : (
            <div className="flex items-center">
              <Send size={16} className="mr-1" /> 
              <span>{getSubmitText()}</span>
            </div>
          )}
        </PrimaryButton>
      )}
      
      {shouldShowReject && (
        <PrimaryButton 
          type="button"
          onClick={onReopen}
          disabled={reopening || disabled}
          className="bg-red-600 hover:bg-red-700"
        >
          {reopening ? (
            <div className="flex items-center">
              <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 414 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Rejecting...</span>
            </div>
          ) : (
            <div className="flex items-center">
              <XCircle size={16} className="mr-1" /> 
              <span>Reject</span>
            </div>
          )}
        </PrimaryButton>
      )}
      
      {shouldShowApprove && onApprove && (
        <PrimaryButton 
          type="button"
          onClick={onApprove}
          disabled={submitting || disabled}
          className="bg-green-600 hover:bg-green-700"
        >
          {submitting ? (
            <div className="flex items-center">
              <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 414 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Approving...</span>
            </div>
          ) : (
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-1" /> 
              <span>Approve</span>
            </div>
          )}
        </PrimaryButton>
      )}
      
      {shouldShowReopen && onReopen && (
        <PrimaryButton 
          type="button"
          onClick={onReopen}
          disabled={reopening || disabled}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {reopening ? (
            <div className="flex items-center">
              <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Reopening...</span>
            </div>
          ) : (
            <div className="flex items-center">
              <RotateCcw size={16} className="mr-1" /> 
              <span>{getReopenText()}</span>
            </div>
          )}
        </PrimaryButton>
      )}
    </div>
  );
};

export default FormButtonBar;